import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Calendar } from 'lucide-react';
import { masterSettingsAPI } from '../../api/api';

const Sessions = () => {
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    start_year: new Date().getFullYear(),
    end_year: new Date().getFullYear() + 4,
    is_current: false,
    course_id: '',
    entry_type: 'Regular'
  });

  useEffect(() => {
    fetchSessions();
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await masterSettingsAPI.getCourses();
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await masterSettingsAPI.getSessions();
      setSessions(response.data);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = (courseId) => {
    const selectedCourse = courses.find(c => c.id === courseId);
    if (selectedCourse) {
      const startYear = new Date().getFullYear();
      const endYear = startYear + selectedCourse.duration_years;
      const sessionName = `${startYear}-${endYear}`;
      setFormData({
        ...formData,
        course_id: courseId,
        course_name: selectedCourse.name,
        start_year: startYear,
        end_year: endYear,
        name: sessionName,
        duration_years: selectedCourse.duration_years
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        course_ids: formData.course_id ? [formData.course_id] : []
      };
      
      if (editingSession) {
        await masterSettingsAPI.updateSession(editingSession.id, submitData);
      } else {
        await masterSettingsAPI.createSession(submitData);
      }
      fetchSessions();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving session:', error);
      alert('Failed to save session');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this session?')) {
      setLoading(true);
      try {
        await masterSettingsAPI.deleteSession(id);
        fetchSessions();
      } catch (error) {
        console.error('Error deleting session:', error);
        alert('Failed to delete session');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (session) => {
    setEditingSession(session);
    setFormData({
      name: session.name,
      start_year: session.start_year,
      end_year: session.end_year,
      is_current: session.is_current || false,
      course_id: session.course_id || (session.course_ids?.length > 0 ? session.course_ids[0] : ''),
      course_name: session.course_name || '',
      duration_years: session.duration_years || 4,
      entry_type: session.entry_type || 'Regular'
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSession(null);
    setFormData({
      name: '',
      start_year: new Date().getFullYear(),
      end_year: new Date().getFullYear() + 4,
      is_current: false,
      course_id: '',
      entry_type: 'Regular'
    });
  };

  const filteredSessions = sessions.filter(session =>
    session.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group sessions by course
  const sessionsByCourse = {};
  filteredSessions.forEach(session => {
    const courseId = session.course_id || 'uncategorized';
    if (!sessionsByCourse[courseId]) {
      sessionsByCourse[courseId] = [];
    }
    sessionsByCourse[courseId].push(session);
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Academic Sessions</h1>
          <p className="text-sm text-gray-500 mt-1">Manage academic year sessions (batch-wise)</p>
        </div>
        <button
          onClick={() => {
            setShowModal(true);
            setEditingSession(null);
          }}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Session
        </button>
      </div>

      <div className="bg-white rounded-lg border p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Search sessions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">No sessions found</p>
            <p className="text-xs text-gray-400 mt-1">Create a session for each course to start admissions</p>
          </div>
        ) : (
          <table className="min-w-full divide-y">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredSessions.map((session) => {
                const linkedCourse = courses.find(c => c.id === session.course_id);
                const courseName = linkedCourse?.name || session.course_name || 'Not Linked';
                const lateralAllowed = linkedCourse?.lateral_entry_allowed;
                
                return (
                  <tr key={session.id}>
                    <td className="px-6 py-4">
                      <span className="font-medium">{session.name}</span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <span className="text-sm font-medium text-gray-900">{courseName}</span>
                        {lateralAllowed && (
                          <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                            Lateral Entry
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-gray-600">{session.start_year} - {session.end_year}</span>
                      <span className="text-xs text-gray-400 block">
                        {session.end_year - session.start_year} Years
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {session.is_current ? (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Current</span>
                      ) : (
                        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Inactive</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => handleEdit(session)} className="text-blue-600 mr-4">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(session.id)} className="text-red-600">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-800 mb-2">📌 How Sessions Work?</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Each Session is linked to a specific Course</li>
          <li>• Session represents an Academic Batch (e.g., 2025-2029)</li>
          <li>• Entry Type (Regular/Lateral) is selected during Student Admission</li>
          <li>• Lateral Entry option appears only if the Course allows it</li>
        </ul>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{editingSession ? 'Edit Session' : 'Add Session'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              
              <div>
                <label className="block text-sm font-medium mb-1">Select Course *</label>
                <select
                  value={formData.course_id}
                  onChange={(e) => handleCourseChange(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">-- Select a Course --</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>
                      {course.name} ({course.code}) - {course.duration_years} Years
                      {course.lateral_entry_allowed ? ' • Lateral' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Session Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="e.g., 2025-2029"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Auto-generated from course duration</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Year *</label>
                  <input
                    type="number"
                    value={formData.start_year}
                    onChange={(e) => {
                      const startYear = parseInt(e.target.value);
                      const duration = formData.duration_years || 4;
                      setFormData({ 
                        ...formData, 
                        start_year: startYear,
                        end_year: startYear + duration,
                        name: `${startYear}-${startYear + duration}`
                      });
                    }}
                    className="w-full px-4 py-2 border rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Year</label>
                  <input
                    type="number"
                    value={formData.end_year}
                    readOnly
                    className="w-full px-4 py-2 border rounded-lg bg-gray-50"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_current"
                  checked={formData.is_current}
                  onChange={(e) => setFormData({ ...formData, is_current: e.target.checked })}
                  className="h-4 w-4 rounded text-green-600"
                />
                <label htmlFor="is_current" className="ml-2 text-sm">Set as current session</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Entry Type</label>
                <div className="flex gap-2">
                  {['Regular', 'Lateral'].map(opt => {
                    const active = (formData.entry_type || 'Regular') === opt;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setFormData({ ...formData, entry_type: opt })}
                        data-testid={`session-entry-type-${opt.toLowerCase()}`}
                        className={`flex-1 px-3 py-2 text-sm rounded-lg border transition-colors ${
                          active
                            ? (opt === 'Lateral'
                                ? 'bg-amber-50 border-amber-400 text-amber-800 font-medium'
                                : 'bg-blue-50 border-blue-400 text-blue-800 font-medium')
                            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {opt === 'Lateral' ? '🔀 Lateral Entry' : '🎓 Regular Entry'}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Mark this session as Lateral-only if it's specifically for lateral admissions (e.g., 3-year track for 4-year course).
                  Use "Regular" for normal sessions — they can be used for both regular and lateral admissions.
                </p>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg disabled:bg-gray-400">
                  {loading ? 'Saving...' : (editingSession ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sessions;
